import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAgentCreationNotifications } from "@/hooks/agent/useAgentCreationNotifications";
import { useVersionCheck } from "@/hooks/app/useVersionCheck";
import { useGenieConsole, useSelectedGenieId } from "@/hooks/dashboard";
import InboxPage from "@/pages/UserHub/Inbox";
import {
  DASHBOARD_LAYOUT_VARIANTS,
  DashboardLayoutVariant,
} from "@/types/dashboard/genie-console";
import { GenieIdentityHeader } from "./components/GenieIdentityHeader";
import { LayoutSwitcher } from "./components/LayoutSwitcher";
import { NextActionLine } from "./components/NextActionLine";
import { resolveGenieBrand } from "./components/genieBrand";
import { FilmstripLayout } from "./components/layouts/FilmstripLayout";
import { SpineLayout } from "./components/layouts/SpineLayout";
import { StageLayout } from "./components/layouts/StageLayout";
import { useLifecycleBands } from "./components/useLifecycleBands";
import { resolveNextAction } from "./nextAction";

const DEFAULT_LAYOUT: DashboardLayoutVariant = "spine";

const isLayoutVariant = (value: string | null): value is DashboardLayoutVariant =>
  Boolean(value) && DASHBOARD_LAYOUT_VARIANTS.includes(value as DashboardLayoutVariant);

/**
 * The Genie console dashboard.
 *
 * HG-1229 the Genie identity header · HG-1231 the computed do-this-next line ·
 * HG-1230 the vertical BRAIN → REACH → VOICE → BUILD lifecycle spine, every
 * band showing a live rendering of the user's own content · HG-1225 three
 * switchable visual directions behind `?layout=`.
 *
 * All data comes from `useGenieConsole`, where each band resolves on its own
 * query — a slow or failed band draws its own state and never blanks the page.
 */
const Dashboard = () => {
  const [searchParams] = useSearchParams();

  useVersionCheck();
  useAgentCreationNotifications();

  if (searchParams.get("home") !== "dashboard") return <InboxPage />;

  return <DashboardConsole />;
};

const DashboardConsole = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { selectedGenieId, selectGenie } = useSelectedGenieId();
  const state = useGenieConsole(selectedGenieId);

  const brand = useMemo(
    () => resolveGenieBrand(state.genie.data.selectedGenie),
    [state.genie.data.selectedGenie],
  );
  const bands = useLifecycleBands(state, brand);
  const nextAction = useMemo(() => resolveNextAction(state.ranking), [state.ranking]);

  // HG-1225: `?layout=` picks a direction. The switcher only appears once the
  // parameter is present, so reviewers get all three from one branch deploy
  // and normal users never see the control.
  const layoutParam = searchParams.get("layout");
  const isReviewingLayouts = layoutParam !== null;
  const layout: DashboardLayoutVariant = isLayoutVariant(layoutParam)
    ? layoutParam
    : DEFAULT_LAYOUT;

  const setLayout = (variant: DashboardLayoutVariant) => {
    const next = new URLSearchParams(searchParams);
    next.set("layout", variant);
    setSearchParams(next, { replace: true });
  };

  // Old dashboard links may still include a tab. Keep any unrelated query parameters.
  useEffect(() => {
    if (!searchParams.has("tab")) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("tab");
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="container mx-auto max-w-[1920px] px-4 pb-8 pt-16">
      <GenieIdentityHeader state={state} onSelectGenie={selectGenie}>
        <NextActionLine action={nextAction} brandColor={brand.primary} />
      </GenieIdentityHeader>

      {isReviewingLayouts && <LayoutSwitcher active={layout} onChange={setLayout} />}

      {layout === "stage" ? (
        <StageLayout bands={bands} />
      ) : layout === "filmstrip" ? (
        <FilmstripLayout bands={bands} />
      ) : (
        <SpineLayout bands={bands} />
      )}
    </div>
  );
};

export default Dashboard;
